import React, { Fragment, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Form from "../../common/Form";
import Row from "../../common/Row";
import Col from "../../common/Col";
import NumberInput from "../../common/NumberInput";
import HourInput from "../../common/HourInput";
import JaNeeRadioButtonGroup from "../../common/JaNeeRadioButtonGroup";
import SelectInput from "../../common/SelectInput";
import TextAreaInput from "../../common/TextAreaInput";
import { getFilesComponentProps } from "../../common/FilesHelperUtil";
import TextInput from "../../common/TextInput";
import { TochtRedenVanStatus, TochtTypeIds, LusTypeIds } from "../../../constants/DomainConstants";
import Files from "../../common/Files";
import Confirm from "../../common/Confirm";
import Table from "../../common/Table";
import Fieldset from "../../common/Fieldset";
import Tooltip from "../../common/Tooltip";
import TochtRouteMap from "../../common/map/TochtRouteMap";
import * as types from "../../../constants/ComponentConstants";
import ValidationMessage from "../../common/ValidationMessage";
import bevoorradingszone from "../../../images/map/bevoorradingszone.png";
import routeImage from "../../../images/map/route.png";
import ANBGebied from "../../../images/map/ANBGebied.png";

const LusForm = (props) => {
	const {
		model,
		lusTypes,
		disabled,
		errors,
		onChange,
		onChangeBevoorradingstentenInANBGebied,
		onSave,
		color,
		GPXBestandFileTypes,
		pijlFotoFileTypes,
		onDropGPXBestand,
		onDeleteGPXBestand,
		onDrop,
		onDelete,
		fileComponents,
		downloadFileUrl,
		onClickDownloadGPXBestand,
		validationMessageType,
		showGPXChangedPopup,
		onGemeenteGewijzigd,
		showAnbPopup,
		onConfirmAnbPopup,
		stratenlijstFilesProps,
		showStratenlijstCSV,
		showGemeentenLijst,
		osmBaseLayerUrl,
		routeModel,
		mapBounds,
		handleRouteUpdated,
		handleRouteCreated,
		onReverseRoute,
		bevoorradingMarkerName,
		handleResetGpx,
		isMapLoading,
		handleRouteEditable,
		handleMarkersEditStart,
		handleDrawingRoute,
		werkelijkeAfstand
	} = props;

	const tochtTypeId = model.tocht.tochtTypeId;
	const isFietstocht = tochtTypeId === TochtTypeIds.FIETSTOCHT;
	const isWandeltocht = tochtTypeId === TochtTypeIds.WANDELTOCHT;
	const isGemotoriseerdeTocht = tochtTypeId === TochtTypeIds.GEMOTORISEERDE_TOCHT;
	const isMennen = tochtTypeId === TochtTypeIds.MENNEN;
	const isMushing = tochtTypeId === TochtTypeIds.MUSHING;

	const lusTypeItems = (lusTypes || [])
		.filter(lusType => lusType.tochtTypeId === tochtTypeId)
		.map(lusType => ({ value: lusType.id, label: lusType.naam }))
		.sort((a, b) => b.label.localeCompare(a.label));
	const showTypeAfstand = lusTypeItems.length > 0;
	const isAnderType = LusTypeIds.ANDERE.includes(model.lusTypeId);

	const toegankelijkheidVraag = isFietstocht
		? { name: "toegankelijkVoorGSporters", label: "Toegankelijk voor G-sporters met eigen fiets ?" }
		: isWandeltocht
			? { name: "toegankelijkVoorRolstoelgebruikers", label: "Toegankelijk voor rolstoelgebruikers en kinderwagens ?" }
			: null;
	const toegankelijk = !!toegankelijkheidVraag && (model[toegankelijkheidVraag.name] === true || model[toegankelijkheidVraag.name] === 'true');

	const verwachtAantalDeelnemersLabel = isGemotoriseerdeTocht ? "Verwacht aantal deelnemers voor deze afstand (voertuigen)"
		: isMennen ? "Verwacht aantal deelnemers voor deze afstand (menners)"
			: isMushing ? "Verwacht aantal deelnemers voor deze afstand (mushers)"
				: "Verwacht aantal deelnemers voor deze afstand";

	let GPXFiles = [];
	if (model.gpxFilename) {
		GPXFiles = [
			{
				display: model.gpxFilename
			}
		];
	}

	function getFilesComponentPropsGPX(title, componentName, downloadFunction, documents, isDownloadOnly, onDrop, onDelete, fileTypes, center, validationMessageType, validationMessage, color, size) {
		const mappedOnDrop = (componentName) => {
			return (files) => onDrop(componentName, files);
		};

		const mappedOnDelete = (componentName) => {
			return (file) => onDelete(componentName, file);
		};


		return {
			title: title,
			componentName: componentName,
			color: color,
			fileTypes: isDownloadOnly || !fileTypes ? null : fileTypes,
			downloadFunction: downloadFunction,
			files: documents,
			onDrop: isDownloadOnly ? null : mappedOnDrop(componentName),
			onDelete: isDownloadOnly ? null : mappedOnDelete(componentName),
			isDownloadOnly: isDownloadOnly,
			center: center === undefined || !center ? false : center,
			validationMessageType: validationMessageType,
			validationMessage: validationMessage,
			size: size === undefined ? null : size,
			pollingEnabled: false
		};
	}

	const lusFilesProps = getFilesComponentPropsGPX(
		"GPX-bestand ",
		"GPX",
		onClickDownloadGPXBestand,
		GPXFiles,
		disabled || hasStep4Status,
		onDropGPXBestand,
		onDeleteGPXBestand,
		GPXBestandFileTypes,
		false, validationMessageType, errors ? errors['gpxFilename'] : null, color, 12);

	const pijlFotoFilesProps = getFilesComponentProps(
		"Foto pijl",
		types.FileComponentNames.FOTOS_PIJLEN,
		downloadFileUrl,
		fileComponents,
		disabled,
		onDrop,
		onDelete,
		pijlFotoFileTypes,
		true, validationMessageType, errors ? errors['pijlFoto'] : null, color, 6);

	let bevoorradingstentenInANBGebied = model.bevoorradingstentenInANBGebied === true || model.bevoorradingstentenInANBGebied === 'true';
	let bepijld = model.bepijld === true || model.bepijld === 'true';

	const hasStep4Status =
		model.tocht.redenVanStatus === TochtRedenVanStatus.InAfwachtigingVanMachtiging ||
		model.tocht.redenVanStatus === TochtRedenVanStatus.MachtigingOK ||
		model.tocht.redenVanStatus === TochtRedenVanStatus.Gepubliceerd;

	const hasFinalStatus =
		model.tocht.redenVanStatus === TochtRedenVanStatus.DoorgestuurdTerGoedkeuring;

	lusFilesProps.isDownloadOnly = disabled || hasStep4Status;

	const volledig = !model.validatiestatusses.filter(x => x.volledig === false).length;
	const anbPopupText = volledig ? "Jouw dossier is klaar voor ANB en zal overgemaakt worden" : "Jouw dossier is nog niet klaar voor ANB en zal niet overgemaakt worden.";


	const conflictLayers = (model.conflicterendeLussen ?? []).map(lus => ({
		id: lus.naamLus,
		data: lus.geoJson,
		showDirection: true,
		style: () => ({ color: lus.color }),
		onEachFeature: (feature, layer) => {
			const label = lus.naamLus;
			if (label) layer.bindTooltip(label, {
				sticky: true,
				direction: 'top',
				offset: [0, -5],
			});

			layer.on('mouseover', () => {
				layer.setStyle({ weight: 8, opacity: 1 });
			});
			layer.on('mouseout', () => {
				layer.setStyle({ weight: 4, opacity: 0.8 });
			});
		}
	}));

	const legendeCustomRows = [
		<Row key={"bevoorradingsZone"}><img style={{ height: '30px', width: '30px', marginLeft: '-9px' }} src={bevoorradingszone} />&nbsp;Bevoorradingszone</Row >,
		<Row key={"ANBGebied"}><img style={{ height: '30px', width: '30px', marginLeft: '-9px' }} src={ANBGebied} />&nbsp;ANB Gebied</Row>,
		<Row key="route"><img style={{ height: '30px', width: '30px', marginLeft: '-9px' }} src={routeImage} />&nbsp;Route</Row>
	];
	const conflicterendeLussenHeaders = ["Naam Tocht - Lus/Afstand", "Conflictgemeente", "Naam + Contactinfo Organisator", ""];
	const conflicterendeLussenTableHeaderStyle =   { backgroundColor: '#e6332a', color: '#fff' };
	const tableHeaderSizesconflicterendeLussen = [27, 18, 46, 9];
	const conflicterendeLussenRows = model.conflicterendeLussen && model.conflicterendeLussen.length ? createConflicterendeLussenRows(model.conflicterendeLussen) : null;
	function createConflicterendeLussenRows(conflicterendeLussen) {
		return conflicterendeLussen.map((conflicterendeLus, i) => {
			const markerClassName = `fa fa-map-marker marker-small marker-${conflicterendeLus.color}`;
			return (<tr key={conflicterendeLus.id}>
				<td>
					{conflicterendeLus.naamTocht} - {conflicterendeLus.naamLus}
				</td>
				<td>{conflicterendeLus.gemeente}</td>
				<td>{conflicterendeLus.isDoorgestuurdTerGoedkeuring  ?
				"Route niet meer aanpasbaar : Deze tocht heeft de status \"doorgestuurd ter goedkeuring\"" :
					`${conflicterendeLus.organisator}: ${conflicterendeLus.naamContact}, ${conflicterendeLus.emailContact}, ${conflicterendeLus.telefoonContact}`
					}</td>
				<td><div className={markerClassName}></div></td>

			</tr>);
		});
	}

	const mapRef = useRef(null);
	const routeMapRef = useRef(null);

	const onSaveClick = useCallback(() => {
		if (mapRef.current === null) {
			return;
		}

		onSave(mapRef, routeMapRef.current.fitToRoute());
	}, [mapRef]);

	return (
		<Fragment>
			{!showGPXChangedPopup
				||
				<Confirm
					message={"Zijn er wijzigingen aan de gemeenten waardoor de tocht loopt naar aanleiding van de gewijzigde gpx?"}
					title="GPX gewijzigd"
					cancelText="Nee"
					confirmText="Ja"
					callback={onGemeenteGewijzigd}
				/>}
			{!showAnbPopup
				||
				<Confirm
					message={anbPopupText}
					title={"Klaar voor ANB?"}
					callback={onConfirmAnbPopup}
					confirmText="OK"
				/>}
			<Form
				name="lusForm"
				headerVisible={false}
				leftButtonLabel={"Bewaar"}
				leftButtonDisabled={disabled || hasFinalStatus}
				onLeftButtonClick={onSaveClick}
			>
				<Row>
					<Col size={12}>
						<div className="section-paragraph-block">
							<p className="section-paragraph">
								In gebieden van het ANB gelden toegankelijkheidsregels. Je kan deze raadplegen op de website van het Agentschap voor Natuur en Bos.&nbsp;
								<a className="external-link" href="https://natuurenbos.vlaanderen.be/toegankelijkheid-en-activiteiten/toegankelijkheidsregeling-voor-uw-natuurgebied-beheerders/overzicht-goedgekeurde-toegankelijkheidsregelingen" target="_blank">
									<i className="fa fa-external-link"></i>
								</a>
							</p>
							<p className="section-paragraph">
								Geplande wegenwerken en evenementen kan je steeds raadplegen op de website van Geopunt bij hinder-in-kaart.&nbsp;
								<a className="external-link" href="https://www.geopunt.be/hinder-in-kaart" target="_blank">
									<i className="fa fa-external-link"></i>
								</a>
							</p>
							<p className="section-paragraph">
								Houd hier rekening mee bij het plannen van je routes.
							</p>
						</div>
					</Col>
				</Row>
				<Row>
					<NumberInput
						name="afstandKm"
						size={6}
						label="Afstand in km (afgerond naar een geheel getal)"
						value={model.afstandKm}
						noDecimals
						onlyPositive
						onChange={onChange}
						disabled={disabled || hasStep4Status || hasFinalStatus}
						validationMessage={errors ? errors['afstandKm'] : ""}
						validationMessageType={validationMessageType} />

					<NumberInput
						name="verwachtAantalDeelnemers"
						size={6}
						label={verwachtAantalDeelnemersLabel}
						value={model.verwachtAantalDeelnemers}
						noDecimals
						onlyPositive
						onChange={onChange}
						disabled={disabled || hasStep4Status || hasFinalStatus}
						validationMessage={errors ? errors['verwachtAantalDeelnemers'] : ""}
						validationMessageType={validationMessageType} />
				</Row>

				{showTypeAfstand &&
					<Row>
						<SelectInput
							name="lusTypeId"
							size={6}
							label="Type"
							items={lusTypeItems}
							value={model.lusTypeId}
							onChange={onChange}
							disabled={disabled || hasStep4Status || hasFinalStatus}
							validationMessage={errors ? errors["lusTypeId"] : ""}
							validationMessageType={validationMessageType} />

						{isAnderType &&
							<TextInput
								name="anderType"
								size={6}
								label="Ander type"
								value={model.anderType}
								onChange={onChange}
								disabled={disabled || hasStep4Status || hasFinalStatus}
								validationMessage={errors ? errors['anderType'] : ""}
								validationMessageType={validationMessageType} />}
					</Row>}

				{toegankelijkheidVraag &&
					<Fragment>
						<Row>
							<JaNeeRadioButtonGroup
								size={6}
								labelInside
								label={toegankelijkheidVraag.label}
								name={toegankelijkheidVraag.name}
								key={toegankelijkheidVraag.name}
								value={model[toegankelijkheidVraag.name]}
								onChange={onChange}
								horizontal
								disabled={disabled || hasFinalStatus}
								validationMessage={errors ? errors[toegankelijkheidVraag.name] : ""}
								validationMessageType={validationMessageType} />
						</Row>
						{toegankelijk &&
							<Row>
								<TextAreaInput
									size={12}
									height={3}
									label="Toelichting toegankelijkheid (max 500 tekens)"
									name={"toelichtingToegankelijkheid"}
									key={"toelichtingToegankelijkheid"}
									optional
									value={model.toelichtingToegankelijkheid}
									onChange={onChange}
									disabled={disabled || hasFinalStatus}
									validationMessage={errors ? errors["toelichtingToegankelijkheid"] : ""}
									validationMessageType={validationMessageType} />
							</Row>}
					</Fragment>}

				<Row>
					<JaNeeRadioButtonGroup
						size={6}
						labelInside
						label="Groepsstart ?"
						name={"groepsstart"}
						key={"groepsstart"}
						value={model.groepsstart}
						onChange={onChange}
						horizontal
						disabled={disabled || hasFinalStatus}
						validationMessage={errors ? errors["groepsstart"] : ""}
						validationMessageType={validationMessageType}
					/>
					{model.groepsstart === true || model.groepsstart === 'true' ? <HourInput
						name="vertrekuurGroepsstart"
						size={6}
						label="vertrekuur groepsstart"
						value={model.vertrekuurGroepsstart}
						onChange={onChange}
						disabled={disabled || hasFinalStatus}
						validationMessage={errors ? errors['vertrekuurGroepsstart'] : ""}
						validationMessageType={validationMessageType} /> : null}
				</Row>
				{model.groepsstart === false || model.groepsstart === 'false' ?
					<Row>
						<HourInput
							name="vertrekUurVan"
							size={6}
							label="Vertrekuur van"
							value={model.vertrekUurVan}
							onChange={onChange}
							disabled={disabled || hasFinalStatus}
							validationMessage={errors ? errors['vertrekUurVan'] : ""}
							validationMessageType={validationMessageType} />
						<HourInput
							name="vertrekUurTot"
							size={6}
							label="Vertrekuur tot"
							value={model.vertrekUurTot}
							onChange={onChange}
							disabled={disabled || hasFinalStatus}
							validationMessage={errors ? errors['vertrekUurTot'] : ""}
							validationMessageType={validationMessageType} />
					</Row> : null}
				<Row>
					<JaNeeRadioButtonGroup
						size={6}
						labelInside
						label={"Wordt de route bepijld ?"}
						name={"bepijld"}
						key={"bepijld"}
						value={model.bepijld}
						onChange={onChange}
						horizontal
						disabled={disabled || hasFinalStatus}
						validationMessage={errors ? errors["bepijld"] : ""}
						validationMessageType={validationMessageType}
					/>
					{bepijld &&
						<TextInput
							name="beschrijvingPijlen"
							size={6}
							label="Kleur van de pijlen"
							value={model.beschrijvingPijlen}
							onChange={onChange}
							disabled={disabled || hasFinalStatus}
							validationMessage={errors ? errors['beschrijvingPijlen'] : ""}
							validationMessageType={validationMessageType} />
					}

				</Row>
				{bepijld &&
					<Fragment>
						<Row>
							<NumberInput
								name="lengtePijlen"
								size={6}
								label="Lengte van de pijlen in cm"
								value={model.lengtePijlen}
								onChange={onChange}
								disabled={disabled || hasStep4Status || hasFinalStatus}
								validationMessage={errors ? errors['lengtePijlen'] : ""}
								validationMessageType={validationMessageType} />
							<NumberInput
								name="breedtePijlen"
								size={6}
								label="Breedte van de pijlen in cm"
								value={model.breedtePijlen}
								onChange={onChange}
								disabled={disabled || hasFinalStatus}
								validationMessage={errors ? errors['breedtePijlen'] : ""}
								validationMessageType={validationMessageType} />
						</Row>
						<Row>
							<Files {...pijlFotoFilesProps} disabled={disabled || hasStep4Status || hasFinalStatus} optional />
						</Row>
					</Fragment>
				}
				<Row>
					<JaNeeRadioButtonGroup
						size={12}
						labelInside
                        label="Worden er bevoorradingstenten geplaatst op openbaar domein?"
						name={"bevoorradingstentenInANBGebied"}
						key={"bevoorradingstentenInANBGebied"}
						value={model.bevoorradingstentenInANBGebied}
						onChange={onChangeBevoorradingstentenInANBGebied}
						horizontal
						disabled={disabled || hasStep4Status || hasFinalStatus}
						validationMessage={errors ? errors["bevoorradingstentenInANBGebied"] : ""}
						validationMessageType={validationMessageType}
					/>
				</Row>
				{bevoorradingstentenInANBGebied &&
					<Fragment>
						<Row>
							&nbsp;&nbsp;&nbsp;Duid de locatie van de bevoorradingstenten aan op de kaart.
							<br /><br />
						</Row>
						<Row>
							<TextAreaInput
								size={12}
								height={3}
								label="Afmetingen van de bevoorradingstenten en de gemeente(n) waarin deze tenten geplaatst worden."
								name={"afmetingenBevoorradingstenten"}
								key={"afmetingenBevoorradingstenten"}
								value={model.afmetingenBevoorradingstenten}
								onChange={onChange}
								disabled={disabled || hasStep4Status || hasFinalStatus}
								validationMessage={errors ? errors["afmetingenBevoorradingstenten"] : ""}
								validationMessageType={validationMessageType}
							/>
						</Row>
					</Fragment>
				}
				{!(model.conflicterendeLussen && model.conflicterendeLussen.length) ||
					<Fragment>
						<Row>
							<Col size={12}>
								<p className="orange-text">
									<strong>Deze GPX is in conflict met routes van 1 of meerdere andere tochten die op dezelfde dag doorgaan.</strong>
									<br />
									In de onderstaande lijst vind je het overzicht van de gedetecteerde conflicten.
									Je eigen route is in het rood weergegeven op de kaart.
									Wijzig je route op de kaart of neem contact op met de organisator(en) van deze tocht(en) om routewijzigingen af te spreken en laad de gewijzigde route op.
									Jouw gewijzigde route zal daarna opnieuw gecontroleerd worden op conflicten.
									Als de andere tocht reeds de status "doorgestuurd ter goedkeuring" heeft zijn de routes van die tocht niet meer aanpasbaar.
								</p>
							</Col>
						</Row>
						<Row>
							<Col size={12}>
								<Table headers={conflicterendeLussenHeaders} headerSizes={tableHeaderSizesconflicterendeLussen} headerStyle={conflicterendeLussenTableHeaderStyle}>
									{conflicterendeLussenRows}
								</Table>
							</Col>
						</Row>
					</Fragment>

				}
				<Row>
					<Files
						{...lusFilesProps}
						disabled={disabled || hasStep4Status || hasFinalStatus}
						validationMessage={errors ? (errors["gpxContent"] ? errors["gpxContent"] : errors["gpxFilename"]) : ""}
						validationMessageType={types.ValidationMessageTypes.WARNING} />
				</Row>
				{!showStratenlijstCSV ||
					<Row>
						{model.straten.length > 0 ?
							<Files {...stratenlijstFilesProps} />
							:
							null
						}
					</Row>}
				<div ref={mapRef}>
					<TochtRouteMap
						ref={routeMapRef}
						baseLayerUrl={osmBaseLayerUrl}
						routeModel={routeModel}
						conflictLayers={conflictLayers}
						bounds={mapBounds}
						legendeRows={legendeCustomRows}
						routeEditable
						isLoading={isMapLoading}
						editButtonDisabled={disabled || hasStep4Status || hasFinalStatus}
						drawStartCenter={model.mapCenter ? [model.mapCenter.y, model.mapCenter.x] : null}
						hasBevoorradingsZones={bevoorradingstentenInANBGebied}
						bevoorradingMarkerName={bevoorradingMarkerName}
						werkelijkeAfstand={werkelijkeAfstand}
						onReverseRoute={onReverseRoute}
						onStartDrawing={handleDrawingRoute}
						onRouteDrawn={handleRouteCreated}
						onRequestEditRoute={handleRouteEditable}
						onMarkersEditStart={handleMarkersEditStart}
						onModelEdited={handleRouteUpdated}
						onCancelEdit={handleResetGpx}
					/>
				</div>
				{model.showANBWarning && model.routeLooptDoorANBGebied ?
					<Fieldset>
						<Row>
							<Col size={8}>
								<ValidationMessage message="Dossiers die minder dan 35 dagen voor de tocht worden ingediend kunnen niet meer door ANB beoordeeld worden. De routes van deze tocht mogen niet door ANB gebied lopen. Gelieve je route aan te passen." />
							</Col>
						</Row>
					</Fieldset> : null}
				{model.showAWVWarning && model.routeLooptDoorNWegen ?
					<Fieldset>
						<Row>
							<Col size={8}>
								<ValidationMessage message="Dossiers die minder dan 35 dagen voor de tocht worden ingediend kunnen niet meer door het Agentschap Wegen en Verkeer beoordeeld worden. De routes van deze tocht mogen niet langs Gewestwegen/Autosnelwegen lopen of Gewestwegen kruisen. Gelieve je route aan te passen." />
							</Col>
						</Row>
					</Fieldset> : null}
				{showGemeentenLijst && model.gemeenten && model.gemeenten.length ?
					<Fieldset>
						{model.gemeenten.some(x => !x.isLinkedToTocht) &&
							<Row>
								<Col size={12}>
									<ValidationMessage
										type={types.ValidationMessageTypes.WARNING}
										message={"Er werd op basis van de opgegeven route vastgesteld dat de rood gemarkeerde gemeenten ontbreken op de gemeentelijst in de sectie “Tocht”.\nGelieve de route nogmaals na te kijken en/of de gemeentelijst aan te vullen in de sectie “Tocht”.\nKlik in de sectie “Tocht” eerst op de bewaarknop om vervolgens de gemeenten te zien die nog ontbreken."}
									/>
								</Col>
							</Row>}
						<Row>
							<Col size={6}>
								<Table>
									<tr key={'header'} style={{ backgroundColor: '#e6332a' }}>
										<td style={{ color: 'white', fontWeight: 'bold' }}>Gemeenten op je route</td>
									</tr>
									{model.gemeenten.map(x => <tr key={x.id}>
										<td style={x.isLinkedToTocht ? null : { color: types.COLOR_RED }}>{x.naam}
											{x.isLinkedToTocht || <Tooltip
												text={"Er werd op basis van de opgegeven route vastgesteld dat de rood gemarkeerde gemeenten ontbreken op de gemeentelijst in de sectie “Tocht”. Gelieve de route nogmaals na te kijken en/of de gemeentelijst aan te vullen in de sectie “Tocht”. Klik in de sectie “Tocht” eerst op de bewaarknop om vervolgens de gemeenten te zien die nog ontbreken."}
												tooltipType={types.TOOLTIP_ERROR}
												icon={"fa-exclamation-circle"}
												windowWidth={30}
												tooltipColor={types.COLOR_RED}
												iconColor={types.COLOR_RED}
											/>}
										</td>
									</tr>)}
								</Table>
							</Col >
						</Row >
					</Fieldset > : null}
				{model.showANBVragenWarning ?
					<Fieldset>
						<Row>
							<Col size={12}>
								<ValidationMessage
									type={types.ValidationMessageTypes.WARNING}
									message={"Er werd op basis van de opgegeven route vastgesteld dat je route door een ANB gebied loopt.\nKijk in de sectie “Tocht” en vul daar de informatie voor het ANB in."}
								/>
							</Col>
						</Row>
					</Fieldset> : null}
			</Form >
		</Fragment >
	);
};

LusForm.propTypes = {
	model: PropTypes.object,
	lusTypes: PropTypes.array,
	disabled: PropTypes.bool,
	errors: PropTypes.object,
	validationMessageType: PropTypes.string,
	onChange: PropTypes.func,
	onChangeBevoorradingstentenInANBGebied: PropTypes.func,
	onSave: PropTypes.func,
	color: PropTypes.string.isRequired,
	GPXBestandFileTypes: PropTypes.object.isRequired,
	pijlFotoFileTypes: PropTypes.object.isRequired,
	onDropGPXBestand: PropTypes.func.isRequired,
	onDeleteGPXBestand: PropTypes.func.isRequired,
	onDrop: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	fileComponents: PropTypes.object.isRequired,
	downloadFileUrl: PropTypes.string.isRequired,
	onClickDownloadGPXBestand: PropTypes.func.isRequired,
	showGPXChangedPopup: PropTypes.bool,
	onGemeenteGewijzigd: PropTypes.func.isRequired,
	showAnbPopup: PropTypes.bool.isRequired,
	onConfirmAnbPopup: PropTypes.func.isRequired,
	stratenlijstFilesProps: PropTypes.object,
	showStratenlijstCSV: PropTypes.bool,
	showGemeentenLijst: PropTypes.bool,
	osmBaseLayerUrl: PropTypes.string,
	routeModel: PropTypes.object,
	mapBounds: PropTypes.array,
	handleRouteUpdated: PropTypes.func,
	handleRouteCreated: PropTypes.func,
	onReverseRoute: PropTypes.func,
	handleResetGpx: PropTypes.func,
	isMapLoading: PropTypes.bool,
	handleRouteEditable: PropTypes.func,
	handleMarkersEditStart: PropTypes.func,
	handleDrawingRoute: PropTypes.func,
	drawingRoute: PropTypes.bool,
	bevoorradingMarkerName: PropTypes.string.isRequired,
	werkelijkeAfstand: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default LusForm;
